import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, BRAND } from "@/data/site";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "border-b border-white/10 bg-black/80 backdrop-blur-xl" : "bg-transparent"
        }`}
        data-testid="main-header"
      >
        <div className="mx-auto flex h-[76px] max-w-[1440px] items-center justify-between px-5 sm:px-8">
          <Link to="/" aria-label="New Boogie Woogie Academy — Home">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-6 xl:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.path === "/"}
                className={({ isActive }) =>
                  `link-underline font-display text-[11px] font-bold uppercase tracking-[0.18em] transition-colors ${
                    isActive ? "active text-red-500" : "text-zinc-300 hover:text-white"
                  }`
                }
                data-testid={`nav-${l.name.toLowerCase()}`}
              >
                {l.name}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={BRAND.phoneLink}
              className="hidden items-center gap-2 whitespace-nowrap font-mono2 text-xs text-zinc-300 transition-colors hover:text-red-400 lg:flex"
              data-testid="nav-phone"
            >
              <Phone size={14} className="text-red-500" />
              {BRAND.phoneDisplay}
            </a>
            <Link
              to="/booking?mode=enquiry"
              className="hidden bg-gradient-to-r from-red-600 via-rose-600 to-red-700 px-6 py-3 font-display text-[11px] font-extrabold uppercase tracking-[0.16em] text-white shadow-[0_0_20px_rgba(220,38,38,0.35)] transition-all hover:shadow-[0_0_32px_rgba(220,38,38,0.55)] hover:brightness-110 sm:block"
              data-testid="nav-enquire-btn"
            >
              Enquire Now
            </Link>
            <button
              className="flex h-11 w-11 items-center justify-center border border-white/15 text-white xl:hidden"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="nav-menu-open"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-[#050507]/97 backdrop-blur-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-drawer"
          >
            <div className="flex h-[76px] items-center justify-between px-5 sm:px-8">
              <Logo compact />
              <button
                className="flex h-11 w-11 items-center justify-center border border-white/15 text-white"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="nav-menu-close"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex h-[calc(100%-76px)] flex-col justify-center gap-1 overflow-y-auto px-8 pb-24" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.path}
                    end={l.path === "/"}
                    className={({ isActive }) =>
                      `block border-b border-white/8 py-3.5 font-display text-2xl font-black uppercase tracking-tight transition-colors ${
                        isActive ? "text-red-500" : "text-white hover:text-red-400"
                      }`
                    }
                    data-testid={`drawer-${l.name.toLowerCase()}`}
                  >
                    <span className="mr-4 font-mono2 text-[10px] text-red-600">{String(i + 1).padStart(2, "0")}</span>
                    {l.name}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.65 }}
                className="mt-8"
              >
                <Link
                  to="/booking?mode=enquiry"
                  className="block bg-gradient-to-r from-red-600 to-rose-700 py-4 text-center font-display text-sm font-extrabold uppercase tracking-[0.2em] text-white"
                  data-testid="drawer-enquire-btn"
                >
                  Enquire Now
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
